import React, { useEffect, useMemo, useRef, useState } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box } from '@mui/material';
import { size } from 'lodash';

import { Button } from '../Button';
import ConditionalWrapper from '../ConditionalWrapper';
import UploadedFilesContainer from '../UploadedFilesContainer';

import { CustomUploadButtonProps, FileUploaderProps } from './props';

const FileUploader: React.FC<FileUploaderProps> = (props) => {
  const {
    customUploadButton,
    acceptType = 'image/*,.pdf',
    defaultButtonProps,
    defaultButtonText,
    buttonIconProps,
    enableDrop = false,
    itemProps,
    onChange,
    value,
    iconSize,
    allowMultiple = false,
    uploadLimitSize = Infinity,
    disableDefaultIcon = false,
    enablePreview = true,
  } = props;

  // ================ STATES
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState<boolean>(false);
  // ================ HOOKS
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setFiles(value ?? []);
  }, [value]);

  const disabledCondition = useMemo(() => {
    return uploadLimitSize <= size(files);
  }, [uploadLimitSize, files]);

  // ================ EVENTS
  const onHandleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onHandleDrag = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const onHandleDrop = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles: File[] = Array.from(e.dataTransfer.files);
      setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
      onChange?.([...files, ...droppedFiles]);
    }
  };
  const onHandleFileChange = (event: any) => {
    const selectedFiles: File[] = Array.from(event.target.files);
    const fileList = files;
    fileList.push(...selectedFiles);
    setFiles(allowMultiple ? fileList : selectedFiles);
    onChange?.(allowMultiple ? fileList : selectedFiles);
  };

  const onHandleRemove = (index: number) => {
    const fileList = [...files];
    fileList.splice(index, 1);
    setFiles(fileList);
    onChange?.(fileList);
  };

  // ================ VIEWS
  return (
    <Box>
      <ConditionalWrapper
        condition={enableDrop}
        wrapper={(children) => (
          <Box
            onDragEnter={onHandleDrag}
            onDragLeave={onHandleDrag}
            onDragOver={onHandleDrag}
            onDrop={onHandleDrop}
          >
            {children}
          </Box>
        )}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={onHandleFileChange}
          onClick={(event: any) => (event.target.value = null)}
          accept={acceptType}
          multiple={allowMultiple}
        />
        {customUploadButton ? (
          React.isValidElement<CustomUploadButtonProps>(customUploadButton) ? (
            React.cloneElement<CustomUploadButtonProps>(customUploadButton, {
              disabled: disabledCondition,
              onClick: onHandleButtonClick,
              className: dragActive ? 'bg-[#f0f0f0]' : '',
            })
          ) : (
            customUploadButton({
              disabledCondition,
              onHandleButtonClick,
              dragActive,
            })
          )
        ) : (
          <Button
            className={'mb-3'}
            variant={'outlined'}
            onClick={onHandleButtonClick}
            disabled={disabledCondition}
            {...defaultButtonProps}
          >
            {!disableDefaultIcon && (
              <Box {...buttonIconProps}>
                <UploadFileIcon sx={{ fontSize: iconSize }} />
              </Box>
            )}
            {defaultButtonText || 'Upload'}
          </Button>
        )}
      </ConditionalWrapper>
      {enablePreview && (
        <UploadedFilesContainer
          fileList={files}
          itemProps={itemProps}
          onRemove={onHandleRemove}
        />
      )}
    </Box>
  );
};

export * from './props';
export default FileUploader;
